import React from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

// Actions
import { toggleSnack } from "../../state/snackbar/actions";

// Config
import { snackbarTypes } from "../../config/snackbar";
import { submissionStatus } from "../../config/values";
import useTranslate from "../../hooks/useTranslate";

// Icons
import clock from "../../assets/icons/clock.svg";
import copy from "../../assets/icons/copy.svg";
import okTick from "../../assets/icons/ok_tick.svg";

// Components
import LightTooltip from "../LightTooltip/LightTooltip";

const useStyles = makeStyles(theme => ({
  wrapper: {
    marginLeft: "auto",
    display: "flex",
    flexDirection: "row",
    gap: 3
  },
  chip: {
    borderRadius: "17px",
    height: 24,
    color: "white"
  },
  chipActive: {
    backgroundColor: theme.palette.primary.main
  },
  okTick: {
    filter: "brightness(100)",
    width: 14
  },
  tooltipWrapper: {
    padding: theme.spacing(1, 2)
  },
  tooltipList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
  },
  tooltipContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: 30
  },
  iconButton: {
    padding: 8
  },
  buttonIcon: {
    height: 14,
    width: 14
  }
}));

const renderIcon = {
  [submissionStatus.IN_PROGRESS]: clock,
  [submissionStatus.ACCEPTED]: okTick
};

const renderProps = {
  label: {
    [submissionStatus.IN_PROGRESS]:
      "casesPage.caseCard.labels.submissionStatus.Pending",
    [submissionStatus.ACCEPTED]:
      "casesPage.caseCard.labels.submissionStatus.shared"
  },
  tooltipTitle: {
    [submissionStatus.IN_PROGRESS]: "downloadCasePage.referencePendingReview",
    [submissionStatus.ACCEPTED]: "downloadCasePage.referenceNumberValue"
  }
};

const getRenderProps = status => {
  const { label } = renderProps;
  return {
    label: label[status],
    icon: renderIcon[status]
  };
};

const getUIStatusFromList = list => {
  const atLeastOne = list.some(i => i.status === submissionStatus.ACCEPTED);
  return atLeastOne ? submissionStatus.ACCEPTED : submissionStatus.IN_PROGRESS;
};

const StatusChip = ({ status: statusProp, submissions }) => {
  const classes = useStyles();
  const getText = useTranslate();
  const dispatch = useDispatch();

  if (statusProp) {
    if (statusProp === submissionStatus.NONE) return null;

    const { label, icon } = getRenderProps(statusProp);
    return (
      <Chip
        className={clsx(classes.chip, {
          [classes.chipActive]: statusProp === submissionStatus.ACCEPTED
        })}
        icon={<img className={classes.okTick} src={icon} alt="" />}
        label={getText(label)}
      />
    );
  }

  if (!submissions || submissions.length === 0) return null;

  const status = statusProp || getUIStatusFromList(submissions);

  const { label, icon } = getRenderProps(status);

  const handleClickCopy = referenceNumber => () => {
    navigator.clipboard.writeText(referenceNumber);
    dispatch(
      toggleSnack({
        type: snackbarTypes.SUCCESS,
        text: "common.prompts.copiedToClipboard"
      })
    );
  };

  return (
    <LightTooltip
      arrow
      classes={{ tooltip: classes.tooltipWrapper }}
      interactive
      title={
        <div className={classes.tooltipList}>
          {submissions.map(item => (
            <div key={item.id} className={classes.tooltipContent}>
              <span>
                {getText(renderProps.tooltipTitle[item.status], {
                  1: item.referenceNumber
                })}
              </span>
              {item.status === submissionStatus.ACCEPTED && (
                <Tooltip
                  title={getText("common.prompts.copyReference")}
                  placement="right"
                >
                  <IconButton
                    className={classes.iconButton}
                    onClick={handleClickCopy(item.referenceNumber)}
                  >
                    <img className={classes.buttonIcon} src={copy} alt="" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      }
    >
      <Chip
        className={clsx(classes.chip, {
          [classes.chipActive]: status === submissionStatus.ACCEPTED
        })}
        icon={<img className={classes.okTick} src={icon} alt="" />}
        label={getText(label)}
      />
    </LightTooltip>
  );
};

StatusChip.propTypes = {
  status: PropTypes.string,
  submissions: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
      referenceNumber: PropTypes.string
    })
  )
};

StatusChip.defaultProps = {
  status: null,
  submissions: null
};

export default StatusChip;
